import React, {Component} from 'react';
import {withTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {Spinner} from '../../Spinner';
import {ActivityLogEntry} from '../activity_log/ActivityLogEntry';
import {ActivityLogCommentModal} from '../activity_log/ActivityLogCommentModal';
import {fetchCustomMetrics} from '../../../hooks/useCustomMetrics';
import {setCustomMetricLabelsCache} from '../../dashboard/columns';
import {
    LAYER_DEFS,
    LAYER_GROUPS,
    categoriesOfLayers,
    clearDeepLinkParamsFromUrl,
    loadStoredLayers,
    readDeepLinkParams,
    storeLayers,
} from './bitacoraLayers';

// Las capas del timeline (ficha v2, D4), su persistencia y el deep-link viven en `bitacoraLayers.js`
// —lógica pura, sin React—, porque el pulso de la tarjeta «Historial» del acto Memoria tiene que
// pedirle al backend EXACTAMENTE el mismo universo que esta vista lista. Ver el docstring de ese
// archivo: las dos veces que el número y la lista se desincronizaron fue por tener dos definiciones.

const CHANNEL_LABEL_KEYS = {
    GOOGLE: 'client_memory.bitacora.coverage_google',
    FACEBOOK: 'client_memory.bitacora.coverage_meta',
};

// La Bitácora del cliente como vista de la Memoria (ficha v2): timeline unificado por capas,
// con ráfagas colapsadas server-side, agrupado por día y con "cargar más" (sin paginación
// numerada). Encima, el strip de cobertura: qué fuentes alimentan este timeline y cuáles no
// exponen su historial (señal pasiva, nunca un error).
class MemoryBitacoraViewClass extends Component {
    constructor(props) {
        super(props);
        const deepLink = readDeepLinkParams();
        this.state = {
            entries: [],
            loading: true,
            loadingMore: false,
            currentPage: 1,
            totalPages: 1,
            categoryCounts: {},
            coverage: null,
            businessUnits: [],
            filters: {
                bu_id: '',
                date_from: (deepLink && deepLink.dateFrom) || '',
                date_to: (deepLink && deepLink.dateTo) || '',
                search: '',
                // Quién / qué cuenta / qué tipo de evento (card k0FCTVvy). Sus OPCIONES no se
                // arman acá: vienen en la respuesta del feed (`filter_options`), calculadas
                // sobre el mismo queryset que después las autoriza. Inventarlas en el frontend
                // era la forma de ofrecer un filtro que devuelve cero.
                actor: '',
                ad_account_id: '',
                event_type: '',
            },
            filterOptions: {actors: [], ad_accounts: [], event_types: []},
            // La etiqueta de lo que el usuario eligió en cada select, guardada al elegir. Hace
            // falta porque la opción puede DESAPARECER de `filterOptions` cuando el scope se
            // achica, y ahí el select tiene que seguir diciendo qué está filtrando. Ver
            // `renderFacetSelect`.
            selectedLabels: {actor: '', ad_account_id: '', event_type: ''},
            searchDraft: '',
            activeLayers: (deepLink && deepLink.layers.length)
                ? new Set(deepLink.layers)
                : loadStoredLayers(),
            deepLinkApplied: !!deepLink,
            showCommentModal: false,
            isSubmittingComment: false,
            exporting: false,
        };
        this.handleExport = this.handleExport.bind(this);
        this.toggleCommentModal = this.toggleCommentModal.bind(this);
        this.handleCommentSubmit = this.handleCommentSubmit.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.clearDeepLink = this.clearDeepLink.bind(this);
        this.clearAllFilters = this.clearAllFilters.bind(this);
    }

    /**
     * "Limpiar filtros": vuelve la vista a como se abre. Toca TODO lo que acota el feed —los
     * cuatro selects, el rango y la búsqueda— porque un botón que dejara un filtro puesto es
     * peor que no tenerlo: el usuario cree que limpió y sigue mirando un recorte.
     *
     * El rango puede no ser suyo. Cuando la aloja el Historial del AI Booster, la fecha la fija
     * el sismógrafo (`appliedRange`), y ahí el día elegido se muestra arriba, fuera de este
     * componente. Por eso avisa hacia afuera (`onClearAppliedRange`) en vez de borrar el rango
     * por su cuenta: si sólo lo limpiara acá, la barra seguiría marcada y el cartelito diría
     * "estás viendo el 12 de agosto" sobre una lista que ya muestra todo.
     */
    clearAllFilters() {
        if (this.props.onClearAppliedRange) this.props.onClearAppliedRange();
        // Y si se entró por deep-link, también lo deshace: saca los params de la URL (o un F5
        // vuelve a aplicar el rango que el usuario acaba de borrar), apaga el strip que dice
        // "Filtros aplicados desde el listado" y devuelve las capas guardadas del usuario. Antes
        // esto lo hacía SÓLO el otro botón, así que quedaban dos "Limpiar filtros" idénticos en
        // pantalla, uno de los cuales limpiaba a medias.
        if (this.state.deepLinkApplied) clearDeepLinkParamsFromUrl();
        this.setState(prev => ({
            deepLinkApplied: false,
            activeLayers: prev.deepLinkApplied ? loadStoredLayers() : prev.activeLayers,
            filters: {bu_id: '', date_from: '', date_to: '', search: '',
                      actor: '', ad_account_id: '', event_type: ''},
            selectedLabels: {actor: '', ad_account_id: '', event_type: ''},
            searchDraft: '',
        }), () => {
            this.publishActiveCategories();
            this.publishServerFilters();
            this.refetchFromStart();
        });
    }

    /**
     * ¿Hay algo puesto que "Limpiar filtros" pueda sacar?
     *
     * `deepLinkApplied` cuenta: un deep-link puede traer SÓLO capas (`?layers=alerts`, que es como
     * abren la Bitácora el Listado y la campanita) y entonces ningún filtro de los de abajo está
     * puesto. Sin esto, el strip decía «Filtros aplicados desde el listado» y no había un solo
     * control en pantalla para deshacerlo — que es justo lo que el botón del strip hacía antes de
     * unificarse.
     */
    hasActiveFilters() {
        const {filters, searchDraft, deepLinkApplied} = this.state;
        return !!(deepLinkApplied
            || filters.bu_id || filters.date_from || filters.date_to || filters.search
            || filters.actor || filters.ad_account_id || filters.event_type || searchDraft);
    }

    // Alias del de arriba: el strip de deep-link tenía su propio "Limpiar filtros", idéntico en
    // texto y a dos centímetros del otro. Ahora los dos hacen lo mismo — y el strip ya no dibuja
    // el suyo (ver `renderDeepLinkStrip`).
    clearDeepLink() {
        this.clearAllFilters();
    }

    /**
     * `onActiveCategoriesChange` (OPCIONAL): avisa hacia afuera qué capas están activas, como el CSV
     * de `categories` que viaja al backend.
     *
     * Existe porque el pulso de la tarjeta «Historial» del acto Memoria (`MemoryDuo`) **cuenta** lo
     * que esta vista **lista**, y esos dos números tienen que ser el mismo: una barra de N tiene que
     * abrir un día con N filas. Si el usuario prende o apaga un chip acá, el pulso de arriba se
     * vuelve a pedir con las capas nuevas.
     *
     * Sin la prop este método no hace absolutamente nada —ni un request, ni un re-render— y el
     * componente se comporta como siempre (hay test guardián). El valor se publica una vez al montar
     * y después sólo cuando CAMBIA: el padre no puede quedar en un ciclo de re-renders.
     */
    publishActiveCategories() {
        const publish = this.props.onActiveCategoriesChange;
        if (!publish) return;
        const categories = this.activeCategories().join(',');
        if (categories === this._publishedCategories) return;
        this._publishedCategories = categories;
        publish(categories);
    }

    /**
     * `onServerFiltersChange` (OPCIONAL): avisa hacia afuera qué filtros de quién / cuenta / tipo
     * están puestos, ya como query string.
     *
     * Existe por la misma razón que `onActiveCategoriesChange`: el pulso del sismógrafo **cuenta**
     * lo que esta vista **lista**, y una barra de N tiene que abrir un día con N filas. Esa
     * igualdad ya se rompió dos veces por tener la definición de un lado y el otro adivinando; un
     * filtro nuevo que el pulso ignorara la rompería una tercera, y esta vez de la forma más
     * visible: con «Quién = API de Meta» puesto, cada barra estaría contando además todo lo que
     * hicieron las personas.
     *
     * Sin la prop no hace nada (hay test guardián), y sólo publica cuando el valor CAMBIA.
     */
    publishServerFilters() {
        const publish = this.props.onServerFiltersChange;
        if (!publish) return;
        const query = this.serverFiltersQuery();
        if (query === this._publishedServerFilters) return;
        this._publishedServerFilters = query;
        publish(query);
    }

    /**
     * Los tres filtros de facet como query string (sin el `&` inicial). Se arma UNA vez y la usan
     * las dos puntas: el request del feed y lo que se publica para el pulso. Con dos armados
     * distintos, alcanzaba con olvidarse uno para que el número y la lista dejaran de coincidir.
     *
     * El rango y la búsqueda NO están acá a propósito: el rango es la ventana del propio pulso
     * (elegir un día en él es lo que lo setea), y la búsqueda nunca lo afectó.
     */
    serverFiltersQuery() {
        const {filters} = this.state;
        const parts = [];
        if (filters.actor) parts.push('actor=' + encodeURIComponent(filters.actor));
        if (filters.ad_account_id) parts.push('ad_account_id=' + encodeURIComponent(filters.ad_account_id));
        if (filters.event_type) parts.push('event_types=' + encodeURIComponent(filters.event_type));
        return parts.join('&');
    }

    /**
     * `appliedRange` (OPCIONAL, `{from, to}` en YYYY-MM-DD): un rango de fechas que impone quien
     * la aloja. Hoy lo usa el sismógrafo del acto Memoria del AI Booster — el click en un día
     * SETEA el rango de la Bitácora en vez de convivir con otro filtro, que era la forma de
     * quedarse mirando un vacío inexplicable.
     *
     * Sin la prop este método no hace absolutamente nada y el componente se comporta como siempre
     * (hay test guardián). Dos guardas: la referencia, para no reaccionar a renders del padre; y
     * los valores contra el estado actual, para que un `{from, to}` recreado con los mismos
     * valores no dispare un refetch.
     */
    componentDidUpdate(prevProps) {
        if (prevProps.appliedRange === this.props.appliedRange) return;
        const range = this.props.appliedRange;
        const from = (range && range.from) || '';
        const to = (range && range.to) || '';
        const {filters} = this.state;
        if (from === filters.date_from && to === filters.date_to) return;
        this.setState(
            prev => ({filters: {...prev.filters, date_from: from, date_to: to}}),
            () => this.refetchFromStart(),
        );
    }

    componentDidMount() {
        this.publishActiveCategories();
        this.publishServerFilters();
        this.fetchBusinessUnits();
        this.fetchCoverage();
        this.fetchEntries();
        // Alertas de métricas custom: hidratar el cache de display_names para no mostrar el
        // raw "CUSTOM:6" en títulos y frases (mismo patrón que ControlCentralContent).
        fetchCustomMetrics().then((items) => {
            setCustomMetricLabelsCache(items);
            this.setState({customMetricLabelsReady: true});
        });
    }

    fetchBusinessUnits() {
        new APIClient(API_URL + 'client/' + this.props.clientId).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                const bus = (data.data['business_units'] || []).map(bu => ({id: bu.id, name: bu.name}));
                this.setState({businessUnits: bus});
            }
        });
    }

    fetchCoverage() {
        new APIClient(API_URL + 'client/' + this.props.clientId + '/activity-log/coverage/').call('GET')
            .then((data) => {
                if (data && data.status === 'OK') {
                    this.setState({coverage: data.data});
                }
            });
    }

    visibleLayers() {
        // La visibilidad del CHIP de platform changes espera a la coverage (sin módulo, el chip
        // ni existe). El REQUEST no espera: activeCategories() manda la capa igual — el backend
        // la ignora sin módulo, y así el fetch inicial no pierde la capa por la race con coverage.
        const coverage = this.state.coverage;
        const platformEnabled = !!(coverage && coverage.platform_changes && coverage.platform_changes.enabled);
        return LAYER_DEFS.filter(l => !l.requiresPlatformModule || platformEnabled);
    }

    activeCategories() {
        return categoriesOfLayers(this.state.activeLayers);
    }

    /**
     * Todo lo que acota el feed, como query string (sin paginación ni `&` inicial). Lo usan el
     * feed y el export: el CSV tiene que traer exactamente lo que el usuario está mirando, y con
     * dos armados alcanzaba con sumar un filtro de un solo lado para que dejaran de coincidir.
     */
    feedQuery() {
        const {filters} = this.state;
        let query = 'categories=' + this.activeCategories().join(',')
            + '&tz_offset=' + new Date().getTimezoneOffset();
        if (filters.bu_id) query += '&bu_id=' + filters.bu_id;
        if (filters.date_from) query += '&date_from=' + filters.date_from + 'T00:00:00';
        if (filters.date_to) query += '&date_to=' + filters.date_to + 'T23:59:59';
        if (filters.search) query += '&search=' + encodeURIComponent(filters.search);
        const facets = this.serverFiltersQuery();
        if (facets) query += '&' + facets;
        return query;
    }

    handleExport() {
        if (this.state.exporting) return;
        this.setState({exporting: true});
        // `lang`: el CSV sale en el idioma de la pantalla (decisión de fede, review C-HISTORIAL-05).
        // Va sólo acá y no en `feedQuery`, porque no filtra nada.
        const lang = (this.props.i18n && this.props.i18n.language) || 'es';
        const url = API_URL + 'client/' + this.props.clientId + '/activity-log/export/?' + this.feedQuery()
            + '&lang=' + encodeURIComponent(lang);
        // El nombre lo pone también el frontend: si la API se sirve desde otro origen, CORS no
        // expone `Content-Disposition` y el del backend no llega (review C-HISTORIAL-06).
        const today = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const fileName = `bitacora-cliente-${this.props.clientId}-${today.getFullYear()}-`
            + `${pad(today.getMonth() + 1)}-${pad(today.getDate())}.csv`;
        new APIClient(url).download('GET', fileName, (data) => {
            this.setState({exporting: false});
            if (!data || data.status !== 'OK') {
                toast.error(this.props.t('client_memory.bitacora.export_error'));
            }
        }).catch(() => {
            this.setState({exporting: false});
            toast.error(this.props.t('client_memory.bitacora.export_error'));
        });
    }

    fetchEntries(page = 1, append = false) {
        // Token de secuencia: una respuesta vieja (filtros/capas cambiados a mitad, append tardío)
        // se descarta en vez de pisar o mezclarse con el estado más nuevo.
        this._fetchSeq = (this._fetchSeq || 0) + 1;
        const seq = this._fetchSeq;
        this.setState(append ? {loadingMore: true} : {loading: true});
        const url = API_URL + 'client/' + this.props.clientId + '/activity-log/?page=' + page
            + '&page_size=25&' + this.feedQuery();

        new APIClient(url).call('GET').then((data) => {
            if (seq !== this._fetchSeq) return; // llegó tarde: ya hay un fetch más nuevo
            if (data && data.status === 'OK') {
                this.setState((prev) => ({
                    // Dedupe por id: el offset se recalcula sobre grupos colapsados que pueden
                    // correrse entre páginas — sin esto, "Cargar más" duplicaba filas de borde.
                    entries: append
                        ? prev.entries.concat(
                            data.data.entries.filter(e => !prev.entries.some(p => p.id === e.id)))
                        : data.data.entries,
                    totalPages: data.data.total_pages,
                    currentPage: data.data.current_page,
                    categoryCounts: data.data.category_counts || {},
                    // Las opciones viajan con el feed: si el backend todavía no las manda (deploy
                    // a mitad de camino), quedan las que había y los selects no desaparecen.
                    filterOptions: data.data.filter_options || prev.filterOptions,
                    loading: false,
                    loadingMore: false,
                }));
            } else {
                this.setState({loading: false, loadingMore: false});
            }
        });
    }

    refetchFromStart() {
        this.fetchEntries(1, false);
    }

    toggleLayer(layerKey) {
        this.setState((prev) => {
            const next = new Set(prev.activeLayers);
            if (next.has(layerKey)) next.delete(layerKey);
            else next.add(layerKey);
            storeLayers(next);
            return {activeLayers: next};
        }, () => {
            this.publishActiveCategories();
            this.refetchFromStart();
        });
    }

    updateFilter(field, value) {
        this.setState((prev) => ({
            filters: {...prev.filters, [field]: value},
            selectedLabels: {...prev.selectedLabels, [field]: this.facetLabelOf(field, value)},
        }), () => {
            this.publishServerFilters();
            this.refetchFromStart();
        });
    }

    /** El texto de la opción recién elegida, para poder seguir mostrándola si después desaparece
     *  de las opciones. Se resuelve contra las opciones VIGENTES, que son las que el usuario
     *  tiene delante en el momento de elegir. */
    facetLabelOf(field, value) {
        if (!value) return '';
        const options = this.state.filterOptions || {};
        if (field === 'actor') {
            return this.actorLabel((options.actors || []).find(o => String(o.key) === String(value)));
        }
        if (field === 'ad_account_id') {
            const account = (options.ad_accounts || []).find(o => String(o.id) === String(value));
            return (account && account.name) || '';
        }
        if (field === 'event_type') {
            const type = (options.event_types || []).find(o => String(o.event_type) === String(value));
            return type ? this.eventTypeLabel(type) : '';
        }
        return '';
    }

    submitSearch() {
        const value = this.state.searchDraft.trim();
        if (value === this.state.filters.search) return; // sin cambio: no refetchear (blur+Enter)
        this.updateFilter('search', value);
    }

    handleLoadMore() {
        this.fetchEntries(this.state.currentPage + 1, true);
    }

    toggleCommentModal() {
        this.setState({showCommentModal: !this.state.showCommentModal});
    }

    handleCommentSubmit(commentText, buId) {
        this.setState({isSubmittingComment: true});
        const body = {comment_text: commentText};
        if (buId) body.business_unit_id = buId;
        new APIClient(API_URL + 'client/' + this.props.clientId + '/activity-log/').call('POST', body)
            .then((data) => {
                this.setState({isSubmittingComment: false});
                if (data && data.status === 'OK') {
                    this.setState({showCommentModal: false});
                    this.refetchFromStart();
                } else {
                    toast.error((data && data.error_message) || this.props.t('client_memory.bitacora.comment_error'));
                }
            });
    }

    layerCount(layer) {
        const counts = this.state.categoryCounts;
        return layer.categories.reduce((sum, cat) => sum + (counts[cat] || 0), 0);
    }

    // ---- agrupación por día (fecha efectiva local) ----

    entryEffectiveDate(entry) {
        return new Date(entry.last_seen || entry.event_datetime || entry.created_on);
    }

    dayLabel(date) {
        const {t, i18n} = this.props;
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);
        const sameDay = (a, b) => a.toDateString() === b.toDateString();
        if (sameDay(date, today)) return t('client_memory.bitacora.today');
        if (sameDay(date, yesterday)) return t('client_memory.bitacora.yesterday');
        return date.toLocaleDateString(i18n.language, {day: 'numeric', month: 'long', year: 'numeric'});
    }

    groupEntriesByDay() {
        const groups = [];
        let currentKey = null;
        this.state.entries.forEach((entry) => {
            const date = this.entryEffectiveDate(entry);
            const key = date.toDateString();
            if (key !== currentKey) {
                groups.push({key, label: this.dayLabel(date), entries: []});
                currentKey = key;
            }
            groups[groups.length - 1].entries.push(entry);
        });
        return groups;
    }

    // ---- render ----

    renderCoverageStrip() {
        const {t} = this.props;
        const coverage = this.state.coverage;
        if (!coverage || !coverage.platform_changes || !coverage.platform_changes.enabled) return null;
        const channels = coverage.platform_changes.channels || [];
        const unsupported = coverage.platform_changes.unsupported_channels || [];
        // Se dibuja también cuando NO hay ningún canal con historial pero sí canales que no lo
        // publican: es justamente el cliente que corre sólo TikTok o LinkedIn, el que motivó el
        // aviso. Con el guard viejo (`channels.length === 0 → null`) el mensaje aparecía sólo en
        // clientes mixtos, o sea en los que menos falta hacía.
        if (channels.length === 0 && unsupported.length === 0) return null;
        const meetings = coverage.meetings || {};
        return (
            <div className="cmem-coverage">
                <span className="cmem-coverage-title">{t('client_memory.bitacora.coverage_title')}</span>
                <span className="cmem-coverage-item">Dashbo <i className="fas fa-check -ok"/></span>
                {channels.map((channel) => {
                    const label = t(CHANNEL_LABEL_KEYS[channel.channel] || 'client_memory.bitacora.coverage_channel');
                    const hasBlindSpot = channel.accounts_not_exposing > 0;
                    const {i18n} = this.props;
                    const since = channel.earliest_event
                        ? new Date(channel.earliest_event).toLocaleDateString(i18n.language)
                        : null;
                    return (
                        <span key={channel.channel} className="cmem-coverage-item info-icon-container">
                            {label}{' '}
                            {hasBlindSpot
                                ? <i className="fas fa-exclamation-triangle -warn"/>
                                : <i className="fas fa-check -ok"/>}{' '}
                            <span className="cmem-coverage-detail">
                                {hasBlindSpot
                                    ? t('client_memory.bitacora.coverage_partial', {
                                        synced: channel.accounts_synced,
                                        total: channel.accounts_total,
                                        not_exposing: channel.accounts_not_exposing,
                                    })
                                    : t('client_memory.bitacora.coverage_accounts', {count: channel.accounts_total})}
                            </span>
                            {since && (
                                <div className="warning-tooltip -below">
                                    <div className="warning-tooltip-body">
                                        <p className="warning-tooltip-text">
                                            {t('client_memory.bitacora.coverage_since', {
                                                date: since, interpolation: {escapeValue: false},
                                            })}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </span>
                    );
                })}
                {meetings.documents_ready > 0 && (
                    <span className="cmem-coverage-item">
                        {t('client_memory.bitacora.coverage_meetings')} <i className="fas fa-check -ok"/>
                    </span>
                )}
                {this.renderUnsupportedChannels()}
            </div>
        );
    }

    /**
     * Qué plataformas de este cliente NO publican su historial de cambios (hoy TikTok, LinkedIn y
     * DV360: no tienen change log; habría que reconstruirlo con snapshot+diff — card a5kHbzBz).
     *
     * El strip decía qué fuentes alimentan la bitácora y se quedaba callado sobre las que no.
     * Para alguien que corre TikTok, un historial sin una sola fila de TikTok se lee como que
     * Dashbo no lo está trayendo — que es un reclamo de soporte, no una limitación de la
     * plataforma. Se nombran sólo los canales que ESTE cliente tiene conectados: enumerar
     * plataformas que no usa sería ruido.
     */
    renderUnsupportedChannels() {
        const {t} = this.props;
        const coverage = this.state.coverage;
        const unsupported = (coverage && coverage.platform_changes
            && coverage.platform_changes.unsupported_channels) || [];
        if (unsupported.length === 0) return null;
        const names = unsupported.map(c => c.display_name || c.channel).join(', ');
        return (
            <span className="cmem-coverage-item -muted">
                {t('client_memory.bitacora.unsupported_channels',
                   {channels: names, interpolation: {escapeValue: false}})}
            </span>
        );
    }

    /**
     * Los chips van agrupados por DÓNDE pasó el cambio —en Dashbo, en las plataformas, o contexto
     * y avisos— y cada grupo lleva su rótulo (card 2BZI0Us8). Un grupo sin chips visibles no se
     * dibuja: sin el módulo de plataformas, un rótulo sin nada debajo sería una promesa vacía.
     *
     * `aria-pressed` es lo único que le dice a un lector de pantalla (y a quien no distingue el
     * color del borde) si la capa está activa (card nBtSHZEF).
     */
    renderLayerChips() {
        const {t} = this.props;
        const visible = this.visibleLayers();
        return (
            <div className="cmem-layers">
                {LAYER_GROUPS.map((group) => {
                    const layers = visible.filter(l => l.group === group);
                    if (layers.length === 0) return null;
                    const groupLabel = t(`client_memory.bitacora.layer_groups.${group}`);
                    return (
                        <div key={group} className={`cmem-layer-group -${group}`}
                             role="group" aria-label={groupLabel}>
                            <span className="cmem-layer-group__label">{groupLabel}</span>
                            {/* Los chips en su propio contenedor: al envolver quedan alineados entre
                                sí y no vuelven debajo del rótulo (QA-VIS-historial-10). */}
                            <div className="cmem-layer-group__chips">
                                {layers.map((layer) => this.renderLayerChip(layer))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderLayerChip(layer) {
        const {t} = this.props;
        const isOn = this.state.activeLayers.has(layer.key);
        const count = this.layerCount(layer);
        return (
            <button
                key={layer.key}
                type="button"
                className={`cmem-layer-chip -${layer.key} ${isOn ? '-on' : ''}`}
                aria-pressed={isOn}
                onClick={() => this.toggleLayer(layer.key)}
            >
                {t(`client_memory.bitacora.layers.${layer.key}`)}
                {count > 0 && <span className="cmem-layer-count">{count}</span>}
            </button>
        );
    }

    /**
     * Los tres selects que salen de los datos: quién, qué cuenta, qué tipo de evento.
     *
     * **Cada uno se dibuja sólo si tiene algo que ofrecer.** Un desplegable con la única opción
     * "todos" no es un filtro, es una promesa incumplida ocupando lugar: en un cliente sin
     * cambios de plataforma no hay cuentas que elegir, y el select de cuentas no aparece.
     *
     * La excepción es cuando el filtro está puesto: aunque el scope se haya achicado tanto que
     * ya no queden opciones, el select se sigue mostrando — si no, el usuario se quedaría con un
     * filtro activo y sin ningún control para sacarlo.
     */
    renderFacetSelects() {
        const {t} = this.props;
        const {filters, filterOptions} = this.state;
        const options = filterOptions || {};
        return (
            <>
                {this.renderFacetSelect({
                    field: 'event_type',
                    allLabel: t('client_memory.bitacora.all_event_types'),
                    values: (options.event_types || []).map(o => String(o.event_type)),
                    children: this.renderEventTypeGroups(options.event_types || []),
                })}
                {this.renderFacetSelect({
                    field: 'actor',
                    allLabel: t('client_memory.bitacora.all_actors'),
                    values: (options.actors || []).map(o => String(o.key)),
                    children: (options.actors || []).map(actor => (
                        <option key={actor.key} value={actor.key}>
                            {this.actorLabel(actor)} ({actor.count})
                        </option>
                    )),
                })}
                {this.renderFacetSelect({
                    field: 'ad_account_id',
                    allLabel: t('client_memory.bitacora.all_ad_accounts'),
                    values: (options.ad_accounts || []).map(o => String(o.id)),
                    children: (options.ad_accounts || []).map(account => (
                        <option key={account.id} value={account.id}>
                            {account.name} ({account.count})
                        </option>
                    )),
                })}
            </>
        );
    }

    /**
     * Un select de facet. Se dibuja si tiene algo que ofrecer o si su filtro está puesto.
     *
     * **Y si el valor elegido ya no está entre las opciones, se le agrega una opción propia.**
     * Ese es el punto: cuando el usuario achica el scope —eligiendo un día del sismógrafo, una
     * línea de control, lo que sea— el backend recalcula las opciones sobre lo que quedó, y la
     * que estaba elegida puede desaparecer. Con `value` controlado y ningún `<option>` que
     * matchee, React deja seleccionada la PRIMERA: el select decía «Quién: todos» mientras el
     * request seguía llevando `actor=…Pablo Qdiez` y la lista salía vacía. El control mentía
     * sobre por qué la pantalla estaba en blanco.
     *
     * Resetear el filtro habría sido peor: el usuario tiene que VER qué está filtrando para
     * poder sacarlo. Así que la opción se sigue mostrando, con «(0)» — que además es información
     * verdadera: en este scope, ese filtro no tiene nada.
     */
    renderFacetSelect({field, allLabel, values, children}) {
        const current = this.state.filters[field];
        const missing = !!current && values.indexOf(String(current)) === -1;
        if (values.length === 0 && !current) return null;
        return (
            <select
                className="form-control"
                value={current}
                onChange={(e) => this.updateFilter(field, e.target.value)}
            >
                <option value="">{allLabel}</option>
                {missing && (
                    <option value={current}>
                        {this.state.selectedLabels[field] || current} (0)
                    </option>
                )}
                {children}
            </select>
        );
    }

    /**
     * Cómo se lee un actor. Las personas se muestran con el nombre que mandó la plataforma (no
     * hay nada que traducir ahí), pero los dos baldes de SISTEMA los escribe el backend en
     * castellano fijo: «API de Meta», «Sistema Dashbo». Por eso cada opción viaja además con su
     * `kind` y su `platform` (el canal crudo), y el texto se arma acá con las claves de canal que
     * la Bitácora ya tenía. El `label` del backend queda de respaldo.
     */
    actorLabel(actor) {
        const {t} = this.props;
        if (!actor) return '';
        if (actor.kind === 'DASHBO_SYSTEM') return t('client_memory.bitacora.actor_dashbo_system');
        if (actor.kind === 'PLATFORM_SYSTEM') {
            const channelKey = CHANNEL_LABEL_KEYS[actor.platform];
            if (channelKey) {
                return t('client_memory.bitacora.actor_platform_system', {platform: t(channelKey)});
            }
        }
        return actor.label;
    }

    /**
     * Los tipos de evento agrupados por naturaleza. El grupo lo decide el backend (sale de la
     * categoría del tipo), así que un tipo nuevo cae solo en el bloque que le toca.
     *
     * El texto lo pone i18n; el `label` que manda el backend es el respaldo para un tipo que
     * todavía no tenga clave — así una fila nueva se lee en castellano en vez de mostrar la
     * constante cruda.
     */
    renderEventTypeGroups(eventTypes) {
        const {t} = this.props;
        const groups = [];
        eventTypes.forEach((option) => {
            let group = groups.find(g => g.key === option.group);
            if (!group) {
                group = {key: option.group, options: []};
                groups.push(group);
            }
            group.options.push(option);
        });
        return groups.map(group => (
            <optgroup key={group.key} label={t('client_memory.bitacora.event_group_' + group.key)}>
                {group.options.map(option => (
                    <option key={option.event_type} value={option.event_type}>
                        {this.eventTypeLabel(option)} ({option.count})
                    </option>
                ))}
            </optgroup>
        ));
    }

    /** El texto de un tipo de evento: i18n, con el `label` del backend de respaldo para un tipo
     *  que todavía no tenga clave — así una fila nueva se lee en castellano en vez de mostrar la
     *  constante cruda. */
    eventTypeLabel(option) {
        return this.props.t('client_memory.bitacora.event_type_' + option.event_type,
                            {defaultValue: option.label});
    }

    renderFilters() {
        const {t} = this.props;
        const {filters, searchDraft, businessUnits} = this.state;
        return (
            <div className="cmem-bitacora-filters">
                <select
                    className="form-control"
                    value={filters.bu_id}
                    onChange={(e) => this.updateFilter('bu_id', e.target.value)}
                >
                    <option value="">{t('client_memory.bitacora.all_business_units')}</option>
                    {businessUnits.map(bu => <option key={bu.id} value={bu.id}>{bu.name}</option>)}
                </select>
                {this.renderFacetSelects()}
                {this.hasActiveFilters() && (
                    <button type="button" className="cmem-bitacora-clear" onClick={this.clearAllFilters}>
                        {t('client_memory.bitacora.clear_filters')}
                    </button>
                )}
                {/* Las dos fechas van envueltas: son un solo control (un rango) y el flex-wrap
                    las partía en renglones distintos y en extremos opuestos, sin ninguna pista de
                    cuál era cuál — el formato nativo dice `mm/dd/yyyy` en una pantalla en
                    español. Con la etiqueta y sin poder separarse, el rango se lee. */}
                <span className="cmem-bitacora-range">
                    <label className="cmem-bitacora-range__field">
                        <span>{t('client_memory.bitacora.date_from')}</span>
                        <input
                            type="date" className="form-control" value={filters.date_from}
                            onChange={(e) => this.updateFilter('date_from', e.target.value)}
                        />
                    </label>
                    <label className="cmem-bitacora-range__field">
                        <span>{t('client_memory.bitacora.date_to')}</span>
                        <input
                            type="date" className="form-control" value={filters.date_to}
                            onChange={(e) => this.updateFilter('date_to', e.target.value)}
                        />
                    </label>
                </span>
                <input
                    type="text" className="form-control cmem-bitacora-search"
                    placeholder={t('client_memory.bitacora.search_placeholder')}
                    value={searchDraft}
                    onChange={(e) => this.setState({searchDraft: e.target.value})}
                    onKeyDown={(e) => { if (e.key === 'Enter') this.submitSearch(); }}
                    onBlur={() => this.submitSearch()}
                />
                <button className="btn -alt -negative cmem-bitacora-comment" onClick={this.toggleCommentModal}>
                    {t('activity_log.add_comment')}
                </button>
            </div>
        );
    }

    renderFeed() {
        const {t} = this.props;
        if (this.state.loading) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }
        const groups = this.groupEntriesByDay();
        if (groups.length === 0) {
            // Dos vacíos, dos causas. "No hay actividad registrada" sobre un cliente con 2.797
            // eventos es falso, y con un filtro puesto es justamente lo que pasaba: el usuario
            // leía que el cliente no tiene nada. Con filtros, el texto dice que fueron los
            // filtros — y la salida está acá mismo, no arriba y lejos.
            const filtered = this.hasActiveFilters();
            return (
                <div className="activity-log-empty">
                    <i className={filtered ? 'fas fa-filter' : 'fas fa-history'}/>
                    {filtered ? t('client_memory.bitacora.no_entries_for_filters')
                              : t('activity_log.no_entries')}
                    {filtered && (
                        <button type="button" className="cmem-bitacora-clear -inline"
                                onClick={this.clearAllFilters}>
                            {t('client_memory.bitacora.clear_filters')}
                        </button>
                    )}
                </div>
            );
        }
        return (
            <div className="activity-log-entries">
                {groups.map((group) => (
                    <div key={group.key} className="cmem-bitacora-day">
                        <div className="cmem-bitacora-day-label">{group.label}</div>
                        {group.entries.map((entry) => (
                            <ActivityLogEntry key={entry.id} entry={entry} clientId={this.props.clientId}/>
                        ))}
                    </div>
                ))}
                {this.state.currentPage < this.state.totalPages && (
                    <div className="cmem-bitacora-more">
                        {this.state.loadingMore
                            ? <Spinner color="positive small"/>
                            : (
                                <button className="btn -negative" onClick={this.handleLoadMore}>
                                    {t('client_memory.bitacora.load_more')}
                                </button>
                            )}
                    </div>
                )}
            </div>
        );
    }

    renderDeepLinkStrip() {
        if (!this.state.deepLinkApplied) return null;
        const {t} = this.props;
        return (
            // Sin botón propio: el "Limpiar filtros" de la fila de filtros ya deshace también el
            // deep-link. Dos botones con el mismo texto a dos centímetros no eran dos opciones,
            // eran una duda.
            <div className="cmem-deeplink-strip">
                <span className="cmem-deeplink-strip__text">{t('client_memory.bitacora.deep_link_notice')}</span>
            </div>
        );
    }

    /**
     * `pageMode` (OPCIONAL): la Bitácora como pantalla propia —la pestaña Historial del cliente—
     * en vez de bandeja del Recorrido. Suma el encabezado y el export; el feed, los filtros y el
     * deep-link son los mismos, así que las dos superficies no pueden contestar distinto.
     */
    renderPageHeader() {
        if (!this.props.pageMode) return null;
        const {t} = this.props;
        return (
            <header className="cmem-bitacora-page-head">
                <div>
                    <h2 className="cmem-bitacora-page-head__title">{t('client_memory.bitacora.page_title')}</h2>
                    <p className="cmem-bitacora-page-head__lede">{t('client_memory.bitacora.page_lede')}</p>
                </div>
                <button type="button" className="btn -alt cmem-bitacora-export"
                        onClick={this.handleExport} disabled={this.state.exporting}>
                    {this.state.exporting
                        ? <Spinner color="negative small"/>
                        : <i className="fas fa-download" aria-hidden="true"/>}
                    {' '}{t('client_memory.bitacora.export_csv')}
                </button>
            </header>
        );
    }

    render() {
        return (
            <div className={'cmem-bitacora' + (this.props.pageMode ? ' -page' : '')}>
                {this.renderPageHeader()}
                {this.renderDeepLinkStrip()}
                {this.renderCoverageStrip()}
                {this.renderLayerChips()}
                {this.renderFilters()}
                {this.renderFeed()}
                <ActivityLogCommentModal
                    isOpen={this.state.showCommentModal}
                    toggle={this.toggleCommentModal}
                    businessUnits={this.state.businessUnits}
                    onSubmit={this.handleCommentSubmit}
                    isSubmitting={this.state.isSubmittingComment}
                />
            </div>
        );
    }
}

export const MemoryBitacoraView = withTranslation()(MemoryBitacoraViewClass);
